import React from 'react';

import i18n from '@client/utils/i18n';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Chip, Popover, Typography } from '@mui/material';

import type { DashboardProjectItem } from './DashboardProject';

interface DashboardIndependentLinkIconProps {
    independentLink: NonNullable<DashboardProjectItem['independentLink']>;
}

function DashboardIndependentLinkIcon({ independentLink }: DashboardIndependentLinkIconProps) {
    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
    const open = Boolean(anchorEl);

    const { enabled, disabledReason, sourceZoneName, sourceProjectName } = independentLink;
    let iconColor = 'success.main';
    if (!enabled && disabledReason === 'user') {
        iconColor = 'text.disabled';
    } else if (disabledReason === 'mapping_error') {
        iconColor = 'error.main';
    }

    return (
        <>
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
            >
                <LinkIcon sx={{ color: iconColor, fontSize: '1.75rem' }} />
                <Typography variant="body2" sx={{ color: iconColor, fontWeight: 500 }}>
                    {i18n('linked_project_independent_badge', sourceZoneName)}
                </Typography>
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
            >
                <Box sx={{ p: 2, minWidth: 240, maxWidth: 340 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: disabledReason ? 1.5 : 0 }}>
                        {i18n('linked_project_independent_description', sourceProjectName, sourceZoneName)}
                    </Typography>
                    {disabledReason === 'mapping_error' && <Chip size="small" color="warning" label={i18n('linked_project_status_mapping_error') as string} />}
                    {disabledReason === 'user' && <Chip size="small" variant="outlined" label={i18n('linked_project_status_disabled') as string} />}
                </Box>
            </Popover>
        </>
    );
}

export default DashboardIndependentLinkIcon;
